/* =========================================================================
   MONDAIN — the WORLD strip: the world photographs as prints thrown down in
   a row, overlapping and tilted, drifting slowly right to left just above
   the footer of every garment / print PDP (Seamas, 2026-09-18).

   LOADS SMARTLY (Seamas: "very slow to load... have assets load left to
   right"): the file list comes from media/world/manifest.js (written by
   tools/world-manifest.py), not from probing the folder; the row of print
   frames is laid out at once — their sizes are CSS, not the images — so
   the drift starts immediately; then the 480px strip copies fill the
   frames left to right, three in flight at a time, each fading in as it
   lands. Without a manifest the loader falls back to probing the folder.

   ORDER: randomized every load; the new uploads (world-13 on) lead, the
   original archive dozen (world-01..12) are mixed into the back 45%; no two
   of a kind side by side; the testimonial screenshots always sit on top
   of the pile. Two copies of the row, one animation travelling exactly one
   row-width — the loop lands on an identical frame. It never pauses.
   Reduced motion: the row stands still and scrolls by hand.

   CLIPS + CREDITS (2026-09-18): the manifest's video entries (short muted
   mp4 loops) join the WORLD rows only — a host opts in with
   data-strip-video; the PDP strip stays photographs. A print with a
   credit prints its @handle small in its bottom-left corner.
   ========================================================================= */
(function () {
  'use strict';
  var hosts = [].slice.call(document.querySelectorAll('[data-mdn-world-strip]'));
  if (!hosts.length) return;

  var PX_PER_S = 14;                 /* a slow drift; a host can set data-strip-speed */
  var OLD_MAX = 12;
  var CONCURRENCY = 4;
  /* which strip copy: 1x for phones and 1x screens, 2x for retina desktops;
     WebP when the browser decodes it, JPEG otherwise */
  var DPR = (window.devicePixelRatio || 1) > 1.3 && window.innerWidth >= 900 ? '2x' : '1x';
  var WEBP = (function () { try { return document.createElement('canvas').toDataURL('image/webp').indexOf('image/webp') === 5; } catch (e) { return false; } })();
  /* THEME SEAM: on Shopify the manifest carries bare asset names and the
     section sets window.MDN_WORLD_BASE to the theme's assets/ URL */
  var BASE = window.MDN_WORLD_BASE || '';
  function stripSrc(it) {
    var st = it.strip;
    if (typeof st === 'string') return BASE + st;                /* probe fallback / gif */
    if (st.webp === false) return BASE + st[DPR];
    return BASE + st[DPR] + (WEBP ? '.webp' : '.jpg');
  }
  var KIND = {}, TOP = {};
  [[13,14,15,16],[20,21,22,23,24],[25,26,27],[32,33,37],[48,49,50,51,53]].forEach(function (g, k) {
    g.forEach(function (i) { KIND[i] = k + 1; });
  });
  function kind(it) { return it.video ? 'clip' : KIND[it.n]; }
  [48,49,50,51,53].forEach(function (i) { TOP[i] = true; });   /* testimonials on top */

  /* ---- the file list: manifest first, folder probe as the fallback ---- */
  function withList(done) {
    if (window.MDN_WORLD) { done(window.MDN_WORLD); return; }
    var s = document.createElement('script');
    s.src = 'media/world/manifest.js';
    s.onload = function () { done(window.MDN_WORLD || []); };
    s.onerror = function () { probe(done); };
    document.head.appendChild(s);
  }
  function probe(done) {
    var EXTS = ['jpg', 'gif', 'webp', 'png'], MAX = 64, found = [], misses = 0;
    (function tryIndex(idx, e) {
      if (idx > MAX || misses >= 3) { done(found); return; }
      var img = new Image();
      var src = 'media/world/world-' + String(idx).padStart(2, '0') + '.' + EXTS[e];
      img.onload = function () { found.push({ n: idx, wall: src, strip: src }); misses = 0; tryIndex(idx + 1, 0); };
      img.onerror = function () { if (e + 1 < EXTS.length) { tryIndex(idx, e + 1); return; } misses++; tryIndex(idx + 1, 0); };
      img.src = src;
    })(1, 0);
  }

  /* ---- the order ---- */
  function shuffle(a) {
    for (var i = a.length - 1; i > 0; i--) { var j = Math.floor(Math.random() * (i + 1)); var t = a[i]; a[i] = a[j]; a[j] = t; }
    return a;
  }
  function order(list) {
    var fresh = [], old = [];
    list.forEach(function (it) { (it.n <= OLD_MAX ? old : fresh).push(it); });
    shuffle(fresh); shuffle(old);
    for (var i = 1; i < fresh.length; i++) {
      var a = kind(fresh[i]), b = kind(fresh[i - 1]);
      if (!a || a !== b) continue;
      for (var j = i + 1; j < fresh.length; j++) {
        if (kind(fresh[j]) !== a) { var t = fresh[i]; fresh[i] = fresh[j]; fresh[j] = t; break; }
      }
    }
    var out = fresh.slice();
    old.forEach(function (it) {
      var lo = Math.floor(out.length * 0.55);
      out.splice(lo + Math.floor(Math.random() * (out.length - lo + 1)), 0, it);
    });
    return out;
  }

  /* ---- the frames: laid out empty, filled left to right ---- */
  function rand(lo, hi) { return lo + Math.random() * (hi - lo); }
  function row(items, credits) {
    var r = document.createElement('div');
    r.className = 'mdn-worldstrip__row';
    for (var i = 0; i < items.length; i++) {
      var f = document.createElement('span');
      f.className = 'mdn-worldstrip__fig';
      f.style.setProperty('--tilt', rand(-4, 4).toFixed(1) + 'deg');
      f.style.setProperty('--lift', rand(-10, 10).toFixed(0) + 'px');
      f.style.setProperty('--size', rand(0.86, 1.14).toFixed(2));
      f.style.setProperty('--lap', rand(-12, -2).toFixed(0) + 'px');   /* a touch of overlap, never over a neighbour's content */
      f.style.setProperty('--ar', String(items[i].ar || 0.8));   /* the photograph's own proportions, from the manifest */
      f.style.zIndex = String(TOP[items[i].n] ? 9 : 1 + Math.floor(Math.random() * 6));
      var img = document.createElement('img');
      img.alt = ''; img.draggable = false; img.decoding = 'async';
      img.setAttribute('data-src', stripSrc(items[i]));
      f.appendChild(img);
      if (items[i].video) {
        f.classList.add('mdn-worldstrip__fig--clip');
        var v = document.createElement('video');
        v.muted = true; v.loop = true; v.playsInline = true; v.preload = 'none';
        v.setAttribute('muted', ''); v.setAttribute('playsinline', ''); v.setAttribute('loop', '');
        v.setAttribute('data-src', BASE + items[i].video);
        f.appendChild(v);
      }
      if (credits && items[i].credit && items[i].showCredit) {   /* the @handles print on WORLD only (Seamas) */
        var c = document.createElement('span');
        c.className = 'mdn-worldstrip__credit';
        c.textContent = items[i].credit;
        f.appendChild(c);
      }
      r.appendChild(f);
    }
    return r;
  }
  /* the first row copy loads left to right, a few in flight; its twin in
     the second copy takes each file the moment it lands (same URL, so the
     browser serves it from cache — the loop's second half costs nothing) */
  function fill(track) {
    var rows = track.children;
    var imgs = [].slice.call(rows[0].querySelectorAll('img[data-src]'));
    var twins = rows[1] ? [].slice.call(rows[1].querySelectorAll('img[data-src]')) : [];
    var next = 0, landed = 0;
    function one() {
      if (next >= imgs.length) { clips(track); return; }
      var i = next++, img = imgs[i], twin = twins[i];
      img.onload = img.onerror = function () {
        img.classList.add('is-in');
        if (twin) { twin.src = img.src; twin.classList.add('is-in'); twin.removeAttribute('data-src'); }
        /* the clips start once the first screenful of prints is in, not
           after the last one on a slow line */
        if (++landed === 12) clips(track);
        one();
      };
      img.src = img.getAttribute('data-src');
      img.removeAttribute('data-src');
    }
    for (var k = 0; k < CONCURRENCY; k++) one();
  }
  /* the clips start once every poster is in: one at a time, left to right,
     each playing over its poster as soon as it can */
  function clips(track) {
    if (track.hasAttribute('data-clips')) return;
    track.setAttribute('data-clips', 'true');
    var rows = track.children;
    var vids = [].slice.call(rows[0].querySelectorAll('video[data-src]'));
    var twins = rows[1] ? [].slice.call(rows[1].querySelectorAll('video[data-src]')) : [];
    function start(v, after) {
      var done = false;
      function go() { if (done) return; done = true; v.classList.add('is-in'); if (after) after(); }
      v.addEventListener('canplay', function () { v.play().then(go, go); }, { once: true });
      v.addEventListener('error', go, { once: true });
      v.src = v.getAttribute('data-src');
      v.removeAttribute('data-src');
      v.load();
    }
    (function nextClip(i) {
      if (i >= vids.length) return;
      start(vids[i], function () {
        if (twins[i]) start(twins[i]);   /* cached by now */
        nextClip(i + 1);
      });
    })(0);
  }

  /* a handle prints on at most two prints per load (Seamas: "way too many
     shoutouts. limit it to 1-2 per person max") */
  var CREDIT_MAX = 1;   /* one per person (Seamas: "no need for duplicate shoutout") */
  function capCredits(items) {
    var seen = {};
    for (var i = 0; i < items.length; i++) {
      var c = items[i].credit;
      if (!c) continue;
      seen[c] = (seen[c] || 0) + 1;
      items[i].showCredit = seen[c] <= CREDIT_MAX;
    }
    return items;
  }
  function build(host, list, ordered) {
    if (list.length < 2) return;
    var speed = parseFloat(host.getAttribute('data-strip-speed')) || PX_PER_S;
    var items = ordered ? list : capCredits(order(list));
    var track = document.createElement('div');
    track.className = 'mdn-worldstrip__track';
    var a = row(items, host.hasAttribute('data-strip-credits')), b = a.cloneNode(true);
    b.setAttribute('aria-hidden', 'true');
    track.appendChild(a); track.appendChild(b);
    host.appendChild(track);

    /* the frames' widths are CSS, so the row measures right before a single
       photograph has arrived — the drift starts now */
    function time() {
      var w = a.getBoundingClientRect().width;
      if (!w) return;
      host.style.setProperty('--mdn-strip-w', w + 'px');
      host.style.setProperty('--mdn-strip-dur', (w / speed) + 's');
      host.setAttribute('data-live', 'true');
    }
    time();
    window.addEventListener('resize', time);
    fill(track);
  }

  /* several hosts (WORLD's two rows) split one shuffled order between them,
     so no photograph appears on more than one row */
  withList(function (all) {
    var wantClips = hosts.some(function (h) { return h.hasAttribute('data-strip-video'); });
    /* WORLD rows take the clips and drop the testimonial screenshots (their
       words rise as message bubbles, assets/world-chat.js); the PDP strip is
       the reverse */
    var list = wantClips ? all.filter(function (it) { return !TOP[it.n]; }) : all.filter(function (it) { return !it.video; });
    if (hosts.length === 1) { build(hosts[0], list); return; }
    var items = capCredits(order(list));
    hosts.forEach(function (h, k) {
      build(h, items.filter(function (_, i) { return i % hosts.length === k; }), true);
    });
  });
})();
